import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TakeActionModal from './leads/TakeActionModal';
import LeadProfile from './leads/components/LeadProfile';
import { Users, PhoneCall, CheckCircle, TrendingUp, Check, Clock, X, ChevronLeft, Activity, Eye, ArrowUpRight, Zap, Target, Star, MoreHorizontal, AlertCircle, Trash2 } from 'lucide-react';
import AddLead from './leads/AddLead';
import DateFilter from '../components/DateFilter';
import PrinterLoader from '../components/PrinterLoader';
import Dropdown from '../components/Dropdown';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getStatusColors } from './../utils/statusUtils';
import { isDateInRange, formatAppDate, formatAppDateOnly } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-hot-toast';

const StatCard = ({ title, value, icon, color, trend, gradient, shadowColor, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex',
      flexDirection: 'column',
      background: gradient || 'var(--color-bg-card)',
      color: gradient ? 'white' : 'var(--color-text-main)',
      border: gradient ? 'none' : '1px solid var(--color-border)',
      borderRadius: '12px',
      padding: '0.75rem',
      position: 'relative',
      overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      boxShadow: gradient && shadowColor ? `0 10px 30px ${shadowColor.replace('1)', '0.3)')}` : 'var(--shadow-md)',
      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-8px)';
      e.currentTarget.style.boxShadow = gradient && shadowColor ? `0 20px 40px ${shadowColor.replace('1)', '0.4)')}` : '0 15px 30px rgba(0,0,0,0.08)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = gradient && shadowColor ? `0 10px 30px ${shadowColor.replace('1)', '0.3)')}` : 'var(--shadow-md)';
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', zIndex: 1 }}>
      <div>
        <h3 style={{ fontSize: '0.9rem', fontWeight: '500', color: gradient ? 'rgba(255,255,255,0.9)' : 'var(--color-text-muted)', marginBottom: '0.25rem' }}>{title}</h3>
        <div style={{ fontSize: '1.5rem', fontWeight: '800', letterSpacing: '-1px', lineHeight: '1.2' }}>
          {value}
        </div>
      </div>
      <div style={{
        padding: '14px',
        borderRadius: '12px',
        backgroundColor: gradient ? 'rgba(255,255,255,0.2)' : `${color}15`,
        color: gradient ? 'white' : color,
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {icon}
      </div>
    </div>


    {/* Decorative background shape */}
    {gradient && (
      <div style={{
        position: 'absolute',
        right: '-15%',
        bottom: '-25%',
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)',
        zIndex: 0
      }} />
    )}
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [activeModal, setActiveModal] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);

  const [showLeadModal, setShowLeadModal] = useState(false);
  const [viewingLead, setViewingLead] = useState(null);
  const [actionDateFilter, setActionDateFilter] = useState('today');
  const [missedDateFilter, setMissedDateFilter] = useState('today');
  const [globalDateFilter, setGlobalDateFilter] = useState('this_month');
  const [selectedUserJourney, setSelectedUserJourney] = useState(null);

  const [showActionModal, setShowActionModal] = useState(false);
  const [isViewMode, setIsViewMode] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileLead, setProfileLead] = useState(null);
  const [actionLead, setActionLead] = useState(null);
  

  const handleViewLead = async (leadId, leadName) => {
    try {
      const res = await api.get(`/leads/${leadId}`);
      const realLead = res.data;

      setViewingLead({
        id: realLead.id,
        name: realLead.companyName,
        contact: realLead.contactPerson,
        phone: realLead.phone,
        email: realLead.email,
        address: realLead.address,
        source: realLead.leadSource?.name || 'Unknown',
        assignee: realLead.user?.name || 'Unassigned',
        machine: realLead.machine?.name || 'N/A',
        type: realLead.leadType?.name ? realLead.leadType.name : (realLead.otherInterest ? `Other (${realLead.otherInterest})` : 'Warm'),
        status: realLead.status?.name || 'Pending',
        createdAt: realLead.createdAt,
        notes: realLead.notes,
        followups: realLead.followups || []
      });
      setShowLeadModal(true);
    } catch (err) {
      console.error("Failed to fetch full lead details", err);
      // Fallback
      setViewingLead({
        id: leadId,
        name: leadName,
        contact: leadName,
        phone: 'N/A',
        email: 'N/A',
        source: 'N/A',
        assignee: 'N/A',
        machine: 'N/A',
        type: 'Warm',
        status: 'New',
        createdAt: new Date().toISOString()
      });
      setShowLeadModal(true);
    }
  };

  const [deleteLeadId, setDeleteLeadId] = useState(null);

  const handleDelete = (id) => {
    setDeleteLeadId(id);
  };

  const confirmDelete = async () => {
    if (!deleteLeadId) return;
    try {
      await api.delete(`/leads/${deleteLeadId}`);
      toast.success('Lead deleted successfully');
      setShowLeadModal(false);
      setDeleteLeadId(null);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to delete lead');
      console.error(error);
    }
  };

  const [loading, setLoading] = useState(true);
  const [leadsData, setLeadsData] = useState([]);
  const [followupsData, setFollowupsData] = useState([]);
  const [journeyData, setJourneyData] = useState([]);
  const [stats, setStats] = useState([
    { title: 'Total Leads', value: '0', icon: <Users size={28} />, color: 'var(--color-primary)', trend: '0%', gradient: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%)', path: '/leads' },
    { title: 'Pending Follow-ups', value: '0', icon: <PhoneCall size={28} />, color: '#f59e0b', trend: '0%' },
    { title: 'Completed Leads', value: '0', icon: <CheckCircle size={28} />, color: '#10b981', trend: '0%' },
    { title: 'Lost Leads', value: '0', icon: <TrendingUp size={28} />, color: '#ef4444', trend: '0%', gradient: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)' }
  ]);
  const [pipelineMetrics, setPipelineMetrics] = useState([]);
  const [sources, setSources] = useState({});

  const [usersList, setUsersList] = useState([]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [usersRes, leadsRes, followupsRes, statusesRes, sourcesRes] = await Promise.all([
        api.get('/auth/users').catch(() => ({ data: [] })),
        api.get('/leads'),
        api.get('/followups').catch(() => ({ data: [] })),
        api.get('/masters/status').catch(() => ({ data: [] })),
        api.get('/masters/lead-sources').catch(() => ({ data: [] }))
      ]);

      setUsersList(usersRes.data.filter(u => u.status === 'Active'));

      let formattedLeads = leadsRes.data.map(lead => ({
        id: lead.id,
        name: lead.companyName,
        contact: lead.contactPerson,
        phone: lead.phone,
        email: lead.email,
        contactPhone: lead.contactPhone,
        contactEmail: lead.contactEmail,
        frontCard: lead.frontCard,
        backCard: lead.backCard,
        address: lead.address,
        address2: lead.address2,
        city: lead.city,
        state: lead.state,
        pincode: lead.pincode,
        latitude: lead.latitude,
        longitude: lead.longitude,
        agencyType: lead.agencyType,
        machine: lead.machine?.name ? `${lead.machine.name}${lead.machineModel ? ' - ' + lead.machineModel : ''}` : 'Unknown',
        type: lead.leadType?.name ? lead.leadType.name : (lead.otherInterest ? `Other (${lead.otherInterest})` : 'Unknown'),
        assignee: lead.user?.name || 'Unassigned',
        status: lead.status?.name || 'New',
        source: lead.leadSource?.name || 'Unknown',
        createdAt: lead.createdAt,
        createdBy: lead.createdBy,
        isCustomer: lead.isCustomer,
        customerType: lead.customerType?.name,
        nextFollowUp: (['complete', 'completed', 'closed', 'lost'].includes((lead.status?.name || lead.status || '').toLowerCase()))
          ? null
          : (lead.followups && lead.followups.length > 0
            ? [...lead.followups]
              .filter(f => f.type !== 'System' && !f.notes?.startsWith('[Completed]') && !f.notes?.startsWith('[Rescheduled]') && !f.notes?.startsWith('[Closed]') && !f.notes?.startsWith('[Cancelled]'))
              .sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date
            : null)
      }));

      if (user?.role !== 'Admin' && !hasPermission('leads:read_all')) {
        formattedLeads = formattedLeads.filter(l => l.assignee === user?.name || l.createdBy === user?.name);
      }
      setLeadsData(formattedLeads);

      let formattedFollowups = followupsRes.data.map(f => ({
        id: f.id,
        leadId: f.leadId,
        rawFollowup: f,
        name: f.lead?.companyName || 'Unknown Lead',
        contact: f.lead?.contactPerson || '',
        phone: f.lead?.phone || '',
        status: f.lead?.status?.name || f.status || 'Pending',
        date: f.date || f.createdAt,
        action: f.type || 'Call',
        assignee: f.lead?.user?.name || 'Unknown',
        purpose: f.notes
      }));

      // Filter out System logs and apply role filtering
      formattedFollowups = formattedFollowups.filter(f => f.action !== 'System');
      if (user?.role !== 'Admin') {
        formattedFollowups = formattedFollowups.filter(f => f.assignee === user?.name || formattedLeads.some(l => l.id === f.leadId));
      }
      setFollowupsData(formattedFollowups);

      // Calculate stats
      const totalLeads = formattedLeads.length;

      const pendingFollowupsCond = f => (!/^\[.*?\]/.test(f.purpose || '') || f.purpose?.startsWith('[Reschedule]')) && !f.purpose?.startsWith('Visit Started') && !f.status?.toLowerCase().includes('won') && !f.status?.toLowerCase().includes('complete') && !f.status?.toLowerCase().includes('closed') && !f.status?.toLowerCase().includes('lost');
      const pendingFollowupsCount = formattedFollowups.filter(pendingFollowupsCond).length;

      const completedFollowups = formattedFollowups.filter(f => f.purpose?.startsWith('[Completed]')).length;

      const wonCond = l => l.isCustomer || l.status?.toLowerCase().includes('won') || l.status?.toLowerCase().includes('complete') || l.status?.toLowerCase().includes('convert');
      const wonLeads = formattedLeads.filter(wonCond).length;

      const lostCond = l => l.status?.toLowerCase().includes('lost') || l.status?.toLowerCase().includes('not interested');
      const lostLeads = formattedLeads.filter(lostCond).length;

      const calculateTrend = (thisMonth, lastMonth) => {
        if (lastMonth === 0) return thisMonth > 0 ? '+100%' : '+0%';
        const diff = thisMonth - lastMonth;
        const percentage = Math.round((diff / lastMonth) * 100);
        return percentage > 0 ? `+${percentage}%` : `${percentage}%`;
      };

      const thisMonthLeads = formattedLeads.filter(l => isDateInRange(l.createdAt, globalDateFilter)).length;
      const lastMonthLeads = formattedLeads.filter(l => isDateInRange(l.createdAt, 'last_month')).length;
      const leadsTrend = calculateTrend(thisMonthLeads, lastMonthLeads);

      const thisMonthPending = formattedFollowups.filter(f => pendingFollowupsCond(f) && isDateInRange(f.date, globalDateFilter)).length;
      const lastMonthPending = formattedFollowups.filter(f => pendingFollowupsCond(f) && isDateInRange(f.date, 'last_month')).length;
      const pendingTrend = calculateTrend(thisMonthPending, lastMonthPending);

      const thisMonthWon = formattedLeads.filter(l => wonCond(l) && isDateInRange(l.updatedAt || l.createdAt, globalDateFilter)).length;
      const lastMonthWon = formattedLeads.filter(l => wonCond(l) && isDateInRange(l.updatedAt || l.createdAt, 'last_month')).length;
      const wonTrend = calculateTrend(thisMonthWon, lastMonthWon);

      const thisMonthLost = formattedLeads.filter(l => lostCond(l) && isDateInRange(l.updatedAt || l.createdAt, globalDateFilter)).length;
      const lastMonthLost = formattedLeads.filter(l => lostCond(l) && isDateInRange(l.updatedAt || l.createdAt, 'last_month')).length;
      const lostTrend = calculateTrend(thisMonthLost, lastMonthLost);

      const hotCustomers = formattedLeads.filter(l => l.customerType === 'Hot').length;
      const thisMonthHot = formattedLeads.filter(l => l.customerType === 'Hot' && isDateInRange(l.updatedAt || l.createdAt, globalDateFilter)).length;
      const lastMonthHot = formattedLeads.filter(l => l.customerType === 'Hot' && isDateInRange(l.updatedAt || l.createdAt, 'last_month')).length;
      const hotTrend = calculateTrend(thisMonthHot, lastMonthHot);

      setStats([
        { title: 'Total Leads', value: thisMonthLeads.toString(), icon: <Users size={28} />, color: 'var(--color-primary)', trend: leadsTrend, gradient: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%)', path: '/leads' },
        { title: 'Pending Follow-ups', value: thisMonthPending.toString(), icon: <PhoneCall size={28} />, color: '#f59e0b', trend: pendingTrend },
        { title: 'Completed Leads', value: thisMonthWon.toString(), icon: <CheckCircle size={28} />, color: '#10b981', trend: wonTrend },
        { title: 'Lost Leads', value: thisMonthLost.toString(), icon: <TrendingUp size={28} />, color: '#ef4444', trend: lostTrend, gradient: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)' },
        { title: 'Hot Customers', value: thisMonthHot.toString(), icon: <Zap size={28} />, color: '#f97316', trend: hotTrend, gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' },
      ]);

      const metricsLeads = globalDateFilter === 'all'
        ? formattedLeads
        : formattedLeads.filter(l => isDateInRange(l.createdAt, globalDateFilter));

      const dynamicMetrics = (statusesRes.data || []).filter(s => s.status === 'Active').map(status => {
        const count = metricsLeads.filter(l => l.status === status.name).length;
        return {
          name: status.name,
          count
        };
      });
      setPipelineMetrics(dynamicMetrics);

      const sourceCounts = {};
      (sourcesRes.data || []).filter(s => s.status === 'Active').forEach(s => {
        sourceCounts[s.name] = 0;
      });

      metricsLeads.forEach(lead => {
        if (lead.source && lead.source !== 'Unknown') {
          sourceCounts[lead.source] = (sourceCounts[lead.source] || 0) + 1;
        }
      });
      setSources(sourceCounts);
    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchDashboardData();
  }, [globalDateFilter]);

  const roleFilteredLeads = globalDateFilter === 'all' ? leadsData : leadsData.filter(l => isDateInRange(l.createdAt, globalDateFilter));
  const filteredLeads = roleFilteredLeads.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 10);
  const basePending = followupsData.filter(f => {
    const p = f.purpose || '';
    return !p.startsWith('[Complete') && !p.startsWith('[Closed]') && !p.startsWith('[Check-In]') && !p.startsWith('Visit Started');
  }).filter(f => {
    const st = f.status?.toLowerCase() || '';
    return !st.includes('complete') && !st.includes('won') && !st.includes('lost') && !st.includes('closed') && !st.includes('hold');
  });

  const now = new Date();
  
  const pendingFollowups = basePending
    .filter(f => new Date(f.date) >= now)
    .filter(f => isDateInRange(f.date, actionDateFilter))
    .sort((a,b) => new Date(a.date) - new Date(b.date));

  const missedFollowups = basePending
    .filter(f => new Date(f.date) < now)
    .filter(f => isDateInRange(f.date, missedDateFilter))
    .sort((a,b) => new Date(b.date) - new Date(a.date));

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const renderFollowupCard = (f, i, isMissed = false) => (
    <div key={f.id} style={{
      padding: '0.75rem',
      border: '1px solid',
      borderRadius: '12px',
      backgroundColor: isMissed ? '#fef2f2' : '#fffbeb',
      borderColor: isMissed ? '#fecaca' : '#fcd34d',
      transition: 'all 0.2s',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem'
    }}
      onClick={() => handleViewLead(f.leadId, f.name)}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: isMissed ? 'rgba(239, 68, 68, 0.1)' : (new Date(f.date).setHours(0,0,0,0) === todayStart.getTime() ? 'rgba(245, 158, 11, 0.1)' : 'rgba(99, 102, 241, 0.1)'), color: isMissed ? 'var(--color-danger)' : (new Date(f.date).setHours(0,0,0,0) === todayStart.getTime() ? 'var(--color-warning)' : 'var(--color-primary)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Activity size={16} />
          </div>
          <div>
            <strong style={{ fontSize: '0.95rem', color: 'var(--color-text-main)', display: 'block', marginBottom: '0.1rem' }}>{f.name}</strong>
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{f.contact} {f.phone && `(${f.phone})`}</span>
          </div>
        </div>
        <span style={{
          padding: '0.15rem 0.5rem',
          borderRadius: '9999px',
          fontSize: '0.65rem',
          fontWeight: '600',
          backgroundColor: getStatusColors(f.status).bg,
          color: getStatusColors(f.status).text,
        }}>
          {f.status || 'Unknown'}
        </span>
      </div>

      {/* {f.purpose && <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}><strong>Notes:</strong> <span title={f.purpose} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{f.purpose}</span></div>} */}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: '0.5rem', borderTop: '1px solid var(--color-border)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>{f.assignee}</span>
          <span style={{ fontSize: '0.75rem', fontWeight: '700', color: isMissed ? 'var(--color-danger)' : '#d97706' }}>
            {formatAppDate(f.date)}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="btn text-muted"
            style={{ padding: '0.35rem 0.6rem', fontSize: '0.75rem', backgroundColor: '#f1f5f9', fontWeight: '600', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            onClick={(e) => { e.stopPropagation(); handleViewLead(f.leadId, f.name); }}
          >
            <Eye size={14} /> View
          </button>
          <button
            className="btn text-primary"
            style={{ padding: '0.35rem 0.6rem', fontSize: '0.75rem', backgroundColor: 'rgba(99, 102, 241, 0.08)', fontWeight: '600', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            onClick={(e) => {
              e.stopPropagation();
              setActionLead(f.rawFollowup.lead);
              setShowActionModal(true);
            }}
          >
            <Activity size={14} /> Action
          </button>
        </div>
      </div>
    </div>
  );

  const activeUsersInDate = [...new Set(journeyData.filter(j => isDateInRange(j.date, 'today')).map(j => j.user))];

  const userOptions = usersList.map(u => ({ value: u.name, label: u.name }));



  return (
    <div style={{ paddingBottom: '2rem', animation: 'fadeIn 0.5s ease-out' }}>
      {loading && <PrinterLoader />}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .hover-row:hover { background-color: #f8fafc; cursor: pointer; }
        .glass-panel { background: rgba(255,255,255,0.7); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.5); }
      `}</style>

      {/* Header Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '800', letterSpacing: '-1px', color: 'var(--color-text-main)', margin: 0 }}>
            Welcome back, <span style={{ color: 'var(--color-primary)' }}>{user?.name || 'User'}</span>
          </h2>
          <p style={{ color: 'var(--color-text-muted)', marginTop: '0.5rem', fontSize: '1rem' }}>Here's what's happening with your agency today.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>

          <DateFilter value={globalDateFilter} onChange={setGlobalDateFilter} />
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
        {stats.map((stat, idx) => (
          <StatCard key={idx} {...stat} onClick={stat.path ? () => navigate(stat.path) : undefined} />
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

        {/* Side-by-Side Follow-ups */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
          {/* Pending Follow-ups */}
          <div className="card" style={{ borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', padding: '1.25rem 1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <h3 style={{ fontSize: '1.15rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                  <Activity size={18} style={{ color: 'var(--color-warning)' }} />
                  Pending Follow-ups
                </h3>
                {pendingFollowups.length > 0 && (
                  <span style={{ backgroundColor: 'var(--color-warning)', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '700' }}>
                    {pendingFollowups.length}
                  </span>
                )}
              </div>
              <DateFilter value={actionDateFilter} onChange={setActionDateFilter} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1, maxHeight: '450px', overflowY: 'auto', paddingRight: '0.5rem' }}>
              {pendingFollowups.length > 0 ? pendingFollowups.map((f, i) => renderFollowupCard(f, i, false)) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>
                  No pending follow-ups for this date.
                </div>
              )}
            </div>
          </div>

          {/* Missed Follow-ups */}
          <div className="card" style={{ borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', padding: '1.25rem 1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <h3 style={{ fontSize: '1.15rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                  <AlertCircle size={18} style={{ color: 'var(--color-danger)' }} />
                  Missed Follow-ups
                </h3>
                {missedFollowups.length > 0 && (
                  <span style={{ backgroundColor: 'var(--color-danger)', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '700' }}>
                    {missedFollowups.length}
                  </span>
                )}
              </div>
              <DateFilter value={missedDateFilter} onChange={setMissedDateFilter} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1, maxHeight: '450px', overflowY: 'auto', paddingRight: '0.5rem' }}>
              {missedFollowups.length > 0 ? missedFollowups.map((f, i) => renderFollowupCard(f, i, true)) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>
                  No missed follow-ups! Great job.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Analytics Section - Pipeline & Sources */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
          {/* Sales Pipeline Funnel */}
          <div className="card glass-panel" style={{ borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', padding: '0.75rem' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: '700', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Activity size={18} style={{ color: 'var(--color-primary)' }} />
              Sales Pipeline
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '220px', overflowY: 'auto', paddingRight: '0.5rem' }}>
              {pipelineMetrics.map((metric, idx) => {
                const stageColors = getStatusColors(metric.name);
                const color = stageColors.dot;
                const percentage = Math.max(5, (metric.count / (roleFilteredLeads.length || 1)) * 100);

                return (
                  <div key={metric.name} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: '100px', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={metric.name}>{metric.name}</div>
                    <div style={{ flex: 1, backgroundColor: '#f1f5f9', height: '20px', borderRadius: '10px', overflow: 'hidden' }}>
                      <div style={{ width: `${percentage}%`, height: '100%', backgroundColor: color, display: 'flex', alignItems: 'center', paddingLeft: '8px', color: 'white', fontSize: '0.7rem', fontWeight: 'bold' }}>
                        {metric.count}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Lead Sources Breakdown */}
          <div className="card glass-panel" style={{ borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', padding: '0.75rem' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: '700', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Star size={18} style={{ color: 'var(--color-primary)' }} />
              Top Lead Sources
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {Object.entries(sources).map(([source, count], idx) => (
                <div key={source} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: idx < Object.keys(sources).length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                  <span style={{ fontWeight: '600' }}>{source}</span>
                  <span style={{ fontWeight: '700', color: 'var(--color-primary)' }}>{Math.round((count / (roleFilteredLeads.length || 1)) * 100)}%</span>
                </div>
              ))}
              {Object.keys(sources).length === 0 && (
                <div style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '0.75rem' }}>No data available</div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Leads */}
        <div className="card glass-panel" style={{ padding: 0, overflow: 'hidden', borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: '0 10px 30px rgba(0,0,0,0.03)' }}>
          <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Target size={18} style={{ color: 'var(--color-primary)' }} />
              Recent Leads
            </h3>
            <button
              style={{ color: 'var(--color-primary)', fontWeight: '600', fontSize: '0.85rem', background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => navigate('/leads')}
            >
              View All →
            </button>
          </div>
          <div style={{ overflowX: 'auto', backgroundColor: 'white' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: '#f8fafc', whiteSpace: 'nowrap' }}>
                  <th style={{ padding: '0.5rem 0.75rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase' }}>Lead No.</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase' }}>Lead Details</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase' }}>Contact</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase' }}>Product Interest</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase' }}>Source</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase' }}>Assignee</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase' }}>Created Date</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase' }}>Next Follow-up</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.length > 0 ? filteredLeads.map((lead, i) => (
                  <tr key={lead.id} className="hover-row" style={{ borderBottom: '1px solid var(--color-border)', transition: 'background-color 0.2s' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <button
                        onClick={() => handleViewLead(lead.id, lead.name)}
                        style={{ color: 'var(--color-primary)', fontWeight: '600', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0, fontSize: 'inherit' }}
                      >
                        #{lead.id}
                      </button>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div className="flex flex-col items-start gap-1">
                        <span style={{ fontWeight: '500' }}>{lead.name}</span>
                        {lead.customerType && (
                          <span style={{
                            padding: '0.15rem 0.4rem',
                            borderRadius: '4px',
                            backgroundColor: lead.customerType === 'Hot' ? '#ffedd5' : lead.customerType === 'Warm' ? '#fef3c7' : '#e0f2fe',
                            color: lead.customerType === 'Hot' ? '#ea580c' : lead.customerType === 'Warm' ? '#d97706' : '#0369a1',
                            fontSize: '0.65rem',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            {lead.customerType}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div>{lead.contact}</div>
                      <div className="text-xs mt-1">
                        <a href={`tel:${lead.phone}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{lead.phone}</a>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {lead.type !== 'Unknown' && <div style={{ fontWeight: '500', color: 'var(--color-text-main)', marginBottom: '0.25rem' }}>{lead.type}</div>}
                      <div style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{lead.machine !== 'Unknown' ? lead.machine : ''}</div>
                    </td>
                    <td style={{ padding: '0.75rem', color: 'var(--color-text-muted)' }}>{lead.source}</td>
                    <td style={{ padding: '0.75rem', color: 'var(--color-text-muted)' }}>{lead.assignee}</td>
                    <td style={{ padding: '0.75rem', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                      {formatAppDateOnly(lead.createdAt)}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {lead.nextFollowUp
                        ? formatAppDateOnly(lead.nextFollowUp)
                        : <span className="text-muted">—</span>}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '9999px',
                        backgroundColor: getStatusColors(lead.status).bg,
                        color: getStatusColors(lead.status).text,
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        whiteSpace: 'nowrap'
                      }}>
                        {lead.status}
                      </span>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="9" style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>No leads found for this date.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>


        {/* User Journey / Team Activity */}
        {/* {user?.role === 'Admin' && (
        <div className="card" style={{ borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', padding: '1.25rem 1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <Users size={18} style={{ color: 'var(--color-primary)' }}/>
              Team User Journey
            </h3>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
             {userOptions.map((member) => {
               const isActive = activeUsersInDate.includes(member.value);
               const activityCount = journeyData.filter(j => isDateInRange(j.date, 'today') && j.user === member.value).length;
               
               return (
                 <div 
                   key={member.value} 
                   style={{
                     padding: '0.75rem',
                     border: '1px solid var(--color-border)',
                     borderRadius: '16px',
                     backgroundColor: isActive ? 'white' : '#f8fafc',
                     opacity: isActive ? 1 : 0.6,
                     cursor: isActive ? 'pointer' : 'not-allowed',
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: 'center',
                     gap: '1rem',
                     transition: 'all 0.2s',
                     boxShadow: isActive ? '0 4px 12px rgba(0,0,0,0.02)' : 'none'
                   }}
                   onClick={() => {
                     if (isActive) {
                       setSelectedUserJourney(member.value);
                       setActiveModal('user_journey');
                     }
                   }}
                   onMouseEnter={(e) => { if (isActive) { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.06)'; e.currentTarget.style.borderColor = 'var(--color-primary-light)'; } }}
                   onMouseLeave={(e) => { if (isActive) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.02)'; e.currentTarget.style.borderColor = 'var(--color-border)'; } }}
                 >
                   <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: isActive ? 'rgba(99, 102, 241, 0.1)' : '#e2e8f0', color: isActive ? 'var(--color-primary)' : 'var(--color-text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: '700' }}>
                     {member.value === 'System' ? <Activity size={28} /> : member.value.charAt(0)}
                   </div>
                   <div style={{ textAlign: 'center' }}>
                     <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: 'var(--color-text-main)' }}>{member.label}</h4>
                     <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: isActive ? 'var(--color-primary)' : 'var(--color-text-muted)', fontWeight: isActive ? '600' : '500' }}>
                       {isActive ? `${activityCount} Activities` : 'No Activity'}
                     </p>
                   </div>
                 </div>
               );
             })}
          </div>
        </div>
        )} */}
      </div>

      {/* Action Modals */}
      {activeModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.2s' }}>
          <div className="card" style={{ width: '100%', maxWidth: '500px', padding: '1.5rem', margin: '0.5rem', backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                {activeModal !== 'select_action' && (
                  <button onClick={() => setActiveModal('select_action')} className="text-muted hover:text-main" title="Go Back">
                    <ChevronLeft size={24} />
                  </button>
                )}
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                  {activeModal === 'select_action' && `Select Action`}
                  {activeModal === 'complete' && `Complete Follow-up`}
                  {activeModal === 'reschedule' && `Reschedule Follow-up`}
                  {activeModal === 'close' && `Close Lead`}
                  {activeModal === 'user_journey' && `${selectedUserJourney}'s Activity`}
                </h3>
              </div>
              <button onClick={() => setActiveModal(null)} style={{ padding: '8px', borderRadius: '50%', backgroundColor: '#f1f5f9', color: 'var(--color-text-muted)' }}><X size={20} /></button>
            </div>

            {selectedLead && (
              <div className="mb-6" style={{ padding: '0.75rem', backgroundColor: '#f8fafc', border: '1px solid var(--color-border)', borderRadius: '12px' }}>
                <div style={{ fontWeight: '700', color: 'var(--color-text-main)', fontSize: '1.1rem' }}>{selectedLead.leadName}</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>{selectedLead.contact} • {selectedLead.phone}</div>
              </div>
            )}

            <div className="flex flex-col gap-4">
              {activeModal === 'select_action' && (
                <div className="flex flex-col" style={{ gap: '1rem' }}>
                  <button className="flex items-center" style={{ padding: '0.75rem', border: '1px solid var(--color-border)', borderRadius: '16px', backgroundColor: 'white', cursor: 'pointer', fontWeight: '600', color: 'var(--color-text-main)', transition: 'all 0.2s', fontSize: '1rem' }} onClick={() => setActiveModal('complete')} onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--color-success)'; e.currentTarget.style.backgroundColor = '#f0fdf4'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(16, 185, 129, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem' }}>
                      <Check size={20} style={{ color: 'var(--color-success)' }} />
                    </div>
                    Mark as Completed
                  </button>
                  <button className="flex items-center" style={{ padding: '0.75rem', border: '1px solid var(--color-border)', borderRadius: '16px', backgroundColor: 'white', cursor: 'pointer', fontWeight: '600', color: 'var(--color-text-main)', transition: 'all 0.2s', fontSize: '1rem' }} onClick={() => setActiveModal('reschedule')} onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--color-primary)'; e.currentTarget.style.backgroundColor = '#eef2ff'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(99, 102, 241, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem' }}>
                      <Clock size={20} style={{ color: 'var(--color-primary)' }} />
                    </div>
                    Reschedule Follow-up
                  </button>
                  <button className="flex items-center" style={{ padding: '0.75rem', border: '1px solid var(--color-border)', borderRadius: '16px', backgroundColor: 'white', cursor: 'pointer', fontWeight: '600', color: 'var(--color-text-main)', transition: 'all 0.2s', fontSize: '1rem' }} onClick={() => setActiveModal('close')} onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--color-danger)'; e.currentTarget.style.backgroundColor = '#fef2f2'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(239, 68, 68, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem' }}>
                      <X size={20} style={{ color: 'var(--color-danger)' }} />
                    </div>
                    Close Lead
                  </button>
                </div>
              )}

              {activeModal === 'user_journey' && (
                <div className="flex flex-col gap-4">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative', maxHeight: '400px', overflowY: 'auto', paddingRight: '0.5rem' }} className="hide-scrollbar">
                    <div style={{ position: 'absolute', left: '23px', top: '20px', bottom: '20px', width: '2px', backgroundColor: 'var(--color-border)', zIndex: 0 }}></div>

                    {journeyData.filter(j => isDateInRange(j.date, 'today') && j.user === selectedUserJourney).map((j, i) => (
                      <div key={j.id} style={{ display: 'flex', gap: '0.75rem', position: 'relative', zIndex: 1 }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: j.type === 'create' ? '#dbeafe' : j.type === 'update' ? '#fef3c7' : j.type === 'complete' ? '#d1fae5' : '#f1f5f9', color: j.type === 'create' ? '#2563eb' : j.type === 'update' ? '#d97706' : j.type === 'complete' ? '#059669' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: '700', fontSize: '1.1rem', border: '4px solid white', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                          {j.user === 'System' ? <Activity size={20} /> : j.user.charAt(0)}
                        </div>
                        <div style={{ flex: 1, padding: '0.75rem', border: '1px solid var(--color-border)', borderRadius: '12px', backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <p style={{ margin: 0, fontSize: '0.95rem', color: 'var(--color-text-main)' }}>
                              <strong style={{ fontWeight: '600' }}>{j.user}</strong> {j.action}
                            </p>
                            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--color-text-muted)', fontWeight: '500' }}>{j.details}</p>
                          </div>
                          <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--color-text-muted)', backgroundColor: '#f8fafc', padding: '4px 10px', borderRadius: '12px', whiteSpace: 'nowrap' }}>
                            {j.time}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-4 mt-8 pt-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <button className="btn" onClick={() => setActiveModal(null)} style={{ border: '1px solid var(--color-border)', backgroundColor: 'white', borderRadius: '10px' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showLeadModal && viewingLead && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 50,
          padding: '2rem',
          animation: 'fadeIn 0.2s'
        }}>
          <div className="hide-scrollbar" style={{
            backgroundColor: 'var(--color-bg-card)',
            width: '100%',
            maxWidth: '1200px',
            maxHeight: '90vh', overflow: 'hidden',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            <AddLead
              onClose={() => setShowLeadModal(false)}
              onSave={() => { setShowLeadModal(false); fetchDashboardData(); }}
              initialData={viewingLead}
              isViewMode={isViewMode}
              onEdit={(lead) => { setShowLeadModal(false); setTimeout(() => { setViewingLead(lead); setIsViewMode(false); setShowLeadModal(true); }, 0); }}
              onDelete={(id) => handleDelete(id)}
              onAction={(lead) => {
                setActionLead(lead);
                setShowActionModal(true);
              }}
            />
          </div>
        </div>
      )}

      {showActionModal && actionLead && (
        <TakeActionModal 
          lead={actionLead} 
          onClose={() => setShowActionModal(false)}
          onSuccess={() => {
            setShowActionModal(false);
            fetchDashboardData();
            if (viewingLead) {
              handleViewLead(viewingLead.id, viewingLead.name);
            }
          }} 
        />
      )}
      
      {showProfileModal && profileLead && (
        <LeadProfile 
          lead={profileLead} 
          onClose={() => setShowProfileModal(false)} 
        />
      )}

      {deleteLeadId && (
        <div className="modal-backdrop">
          <div className="modal-content fade-in" style={{ maxWidth: '400px', padding: '2rem', textAlign: 'center' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <Trash2 size={32} className="text-danger" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-main">Delete Lead</h3>
            <p className="text-muted mb-8 text-sm">Are you sure you want to delete this lead? This action cannot be undone and all associated follow-ups will be permanently removed.</p>
            <div className="flex gap-4 justify-center">
              <button className="btn btn-secondary flex-1" onClick={() => setDeleteLeadId(null)}>
                Cancel
              </button>
              <button className="btn flex-1" onClick={confirmDelete} style={{ backgroundColor: 'var(--color-danger)', color: 'white' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

